<script setup lang="ts">
import { computed, onUnmounted, ref, watch } from 'vue'
import { formatFileSize, matchesAccept } from './file-upload.ts'
import StatusTag from '@taler/ui/StatusTag.vue'

/**
 * A styled replacement for a raw `<input type="file">`: shows the chosen file's name and
 * size, previews an image, accepts a drop, and states what it accepts up front. Fully
 * controlled - `modelValue` is the single source of truth, there is no internal draft the way
 * `AmountInput`'s `clean` ref is, since a `File` is never incrementally typed into.
 *
 * All copy is a caller-supplied prop, the same convention `Select`'s `placeholder` uses:
 * this package may not depend on vue-i18n any more than it may depend on `@taler/domain`.
 */
const props = withDefaults(
  defineProps<{
    modelValue: File | null
    label: string
    /** Native `accept` attribute value - also drives the drag-and-drop type check. */
    accept: string
    /** Human copy of what `accept` allows, e.g. "JPEG, PNG, WebP or GIF, up to 10 MB". */
    acceptHint: string
    browseLabel: string
    dropHint: string
    removeLabel: string
    invalidTypeMessage: string
    disabled?: boolean
    /** Off for a file type where a thumbnail can never apply. */
    preview?: boolean
  }>(),
  { disabled: false, preview: true },
)

const emit = defineEmits<{ 'update:modelValue': [file: File | null] }>()

const dragDepth = ref(0)
const dragActive = computed(() => dragDepth.value > 0)
const invalidType = ref(false)

const previewUrl = ref<string | undefined>(undefined)

function isImage(file: File): boolean {
  return file.type.startsWith('image/')
}

watch(
  () => props.modelValue,
  (file) => {
    if (previewUrl.value !== undefined) URL.revokeObjectURL(previewUrl.value)
    previewUrl.value = props.preview && file !== null && isImage(file) ? URL.createObjectURL(file) : undefined
  },
  { immediate: true },
)

onUnmounted(() => {
  if (previewUrl.value !== undefined) URL.revokeObjectURL(previewUrl.value)
})

function acceptFile(file: File): void {
  if (!matchesAccept(file, props.accept)) {
    invalidType.value = true
    return
  }
  invalidType.value = false
  emit('update:modelValue', file)
}

function onInputChange(event: Event): void {
  const picked = (event.target as HTMLInputElement).files?.[0]
  if (picked !== undefined) acceptFile(picked)
  ;(event.target as HTMLInputElement).value = ''
}

function onDragEnter(): void {
  if (props.disabled) return
  dragDepth.value++
}

function onDragLeave(): void {
  if (dragDepth.value > 0) dragDepth.value--
}

function onDrop(event: DragEvent): void {
  dragDepth.value = 0
  if (props.disabled) return
  const dropped = event.dataTransfer?.files[0]
  if (dropped !== undefined) acceptFile(dropped)
}

function clear(): void {
  invalidType.value = false
  emit('update:modelValue', null)
}
</script>

<template>
  <div class="flex flex-col gap-1">
    <span class="text-[length:var(--text-step-2)] font-medium text-[color:var(--color-text)]">{{
      label
    }}</span>

    <label
      v-if="modelValue === null"
      class="flex flex-col items-center gap-[var(--space-tight)] rounded-[var(--radius-control)] border border-dashed px-[var(--space-block)] py-[var(--space-group)] text-center transition-colors duration-[var(--motion-fast)] has-[:focus-visible]:outline has-[:focus-visible]:outline-[length:var(--ring-focus-width)] has-[:focus-visible]:outline-offset-[var(--ring-focus-offset)] has-[:focus-visible]:outline-[color:var(--color-focus)]"
      :class="[
        dragActive
          ? 'border-[color:var(--color-focus)] bg-[color:var(--color-surface-raised)]'
          : 'border-[color:var(--color-border-control)] bg-[color:var(--color-surface)]',
        disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer',
      ]"
      @dragover.prevent
      @dragenter.prevent="onDragEnter"
      @dragleave.prevent="onDragLeave"
      @drop.prevent="onDrop"
    >
      <input
        type="file"
        class="sr-only"
        :accept="accept"
        :disabled="disabled"
        :aria-label="label"
        @change="onInputChange"
      />
      <svg
        width="24"
        height="24"
        viewBox="0 0 16 16"
        aria-hidden="true"
        class="text-[color:var(--color-text-muted)]"
      >
        <path
          d="M8 10.5V3M8 3 5.2 5.8M8 3l2.8 2.8"
          fill="none"
          stroke="currentColor"
          stroke-width="1.4"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
        <path
          d="M2.5 10.5v2.2a1 1 0 0 0 1 1h9a1 1 0 0 0 1-1v-2.2"
          fill="none"
          stroke="currentColor"
          stroke-width="1.4"
          stroke-linecap="round"
        />
      </svg>
      <p class="text-[length:var(--text-step-3)] text-[color:var(--color-text)]">
        <span class="font-medium text-[color:var(--color-action)]">{{ browseLabel }}</span> {{ dropHint }}
      </p>
      <p class="text-[length:var(--text-step-1)] text-[color:var(--color-text-muted)]">{{ acceptHint }}</p>
    </label>

    <div
      v-else
      class="flex items-center gap-[var(--space-cell)] rounded-[var(--radius-control)] border border-[color:var(--color-border)] bg-[color:var(--color-surface)] p-[var(--space-cell)]"
      aria-live="polite"
    >
      <img
        v-if="previewUrl !== undefined"
        :src="previewUrl"
        alt=""
        aria-hidden="true"
        class="h-12 w-12 shrink-0 rounded-[var(--radius-control)] border border-[color:var(--color-border)] object-cover"
      />
      <svg
        v-else
        width="20"
        height="20"
        viewBox="0 0 16 16"
        aria-hidden="true"
        class="shrink-0 text-[color:var(--color-text-muted)]"
      >
        <path
          d="M4 2h5.2L12 4.8V13a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V3a1 1 0 0 1 1-1Z"
          fill="none"
          stroke="currentColor"
          stroke-width="1.4"
          stroke-linejoin="round"
        />
        <path
          d="M9.2 2v2.8H12"
          fill="none"
          stroke="currentColor"
          stroke-width="1.4"
          stroke-linejoin="round"
        />
      </svg>
      <div class="flex min-w-0 flex-1 flex-col">
        <span class="truncate text-[length:var(--text-step-3)] text-[color:var(--color-text)]">{{
          modelValue.name
        }}</span>
        <span class="text-[length:var(--text-step-1)] text-[color:var(--color-text-muted)]">{{
          formatFileSize(modelValue.size)
        }}</span>
      </div>
      <button
        type="button"
        :aria-label="removeLabel"
        :disabled="disabled"
        class="flex h-[var(--density-target-min)] w-[var(--density-target-min)] shrink-0 items-center justify-center rounded-[var(--radius-control)] text-[color:var(--color-text-muted)] transition-colors duration-[var(--motion-fast)] hover:bg-[color:var(--color-surface-raised)] hover:text-[color:var(--color-text)] disabled:pointer-events-none disabled:opacity-50"
        @click="clear"
      >
        <svg width="14" height="14" viewBox="0 0 16 16" aria-hidden="true">
          <path
            d="M4.5 4.5 11.5 11.5M11.5 4.5 4.5 11.5"
            stroke="currentColor"
            stroke-width="1.6"
            stroke-linecap="round"
          />
        </svg>
      </button>
    </div>

    <StatusTag v-if="invalidType" tone="danger" role="alert">{{ invalidTypeMessage }}</StatusTag>
  </div>
</template>
